import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Sentry from "../config/sentry.js";
import { isSafari } from "react-device-detect";

import BotClient1 from "../services/api/BotClient.js";
import GlobalConfig from "../config/config.js";
import InterviewEndNotification from "../components/InterviewEndNotification.js";
import useBlendShapeStore from "../store/ContextStoreBlendshapes.js";
import TranscriptNew from "../components/Transcript-new.js";
import UserVideoNew from "../components/VideoComponent-new.js";

import "../styles/InterviewConversation.css";

import fixWebmDuration from "fix-webm-duration";
import AzureBlobStorageClient from "../services/azure/AzureBlobStorage.js";
import UltravoxClient from "../services/communication/UltravoxClient.js";
import { MediaRecorderConfig, SessionMode } from "../libs/constants/index.js";
import { uploadBlob } from "../services/azure/azure-functions.js";
import { BotConstants } from "../libs/constants";
import useAppStore from "../store/AppStore.js";
import useLiveAvatarStore from "../store/LiveAvatarStore.js";
import { useBoundStore } from "../store/UseBoundStore1.js";
import TenantMngtClient from "../services/api/TenantMngtClient.js";
import logo from "../assests/images/logo.png";
import ModelViewerV3 from "../components/Model/ModelViewer-v3.js";
import { Mic, MicOff, X } from "react-feather";
import { useDeepgramIntegration } from "../services/deepgram/DeepgramSTT.js";
import DeepgramConfig from "../config/deepgram.js";

let interruptedInputs = [];
let lastRecordingTimestamp = null;
let isLeaving = false;

function ConversationV2() {
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const candidateName = useBoundStore((state) => state.username);
  const mId = useBoundStore((state) => state.mId);
  const is2D = useBoundStore((state) => state.is2D);
  const setIs2D = useBoundStore((state) => state.setIs2D);
  const meetingStatus = useBoundStore((state) => state.meetingStatus);
  const meetingId = useBoundStore((state) => state.meetingId);
  const {
    firstName,
    sasUrl,
    setSasUrl,
    version,
    mode,
    isDesktop,
    setIsDesktop,
    ultravoxJoinUrl,
  } = useBoundStore();
  const botClient = useMemo(() => new BotClient1(), []);
  const tenantMngtClient = useMemo(() => new TenantMngtClient(), []);

  const {
    storageClient,
    setStorageClient,
    avatarVideoStream,
    botAudioOnlyStream,
    displayBotVisualizer,
    conversations,
    addMessage,
    canInitializeMediaStream,
    endInterviewAfterSynthesis,
    isUltravoxAgentNodeAvailable,
    botResponse,
    userResponse,
    setUserResponse,
    interviewEnded,
    setInterviewEnded,
    screenSize,
    setScreenSize,
  } = useAppStore();
  const { avatar, avatarMediaStream } = useLiveAvatarStore();
  let lastFinalTranscript = null;

  const { permissions, setPermission } = useAppStore((state) => ({
    permissions: state.permissions,
    setPermission: state.setPermission,
  }));

  // Deepgram real-time speech recognition
  const {
    isConnected: deepgramConnected,
    isListening: deepgramListening,
    isPaused: deepgramPaused,
    error: deepgramError,
    realtimeTranscript,
    startListening: startDeepgram,
    stopListening: stopDeepgram,
    pauseRecording: pauseDeepgram,
    resumeRecording: resumeDeepgram,
    resetTranscripts: resetDeepgram,
    // override,
  } = useDeepgramIntegration({
    useTokenAuth: true,
    model: DeepgramConfig.MODEL,
    language: DeepgramConfig.LANGUAGE,
    smartFormat: DeepgramConfig.SMART_FORMAT,
    interimResults: DeepgramConfig.INTERIM_RESULTS,
    endpointing: DeepgramConfig.ENDPOINTING,
    vadEvents: DeepgramConfig.VAD_EVENTS,
    maxReconnectAttempts: DeepgramConfig.MAX_RECONNECT_ATTEMPTS,
    micStream: permissions.micStream?.stream, // Use existing microphone stream
    autoStart: true, // We'll control this manually
    // audioContext,
    onRealtimeTranscript: (transcript, isFinal) => {
      if (DeepgramConfig.DEBUG_LOGGING) {
        console.log(
          "[Deepgram] Real-time transcript:",
          transcript,
          "Final:",
          isFinal
        );
      }
      // Update user response with real-time transcript
      if (transcript) {
        const trimmed = getUserResponseWithoutBreakingWord(transcript);
        setUserMessage(trimmed);
        setUserResponse(trimmed);
      }
    },
  });

  const userWebcamVideoRef = useRef();

  // State variables
  const [azureBlobAlreadyCreated, setAzureBlobAlreadyCreated] = useState(false);
  const [userMessage, setUserMessage] = useState([]);
  const [userMessageIndex, setUserMessageIndex] = useState(null);
  const [botMessages] = useState("");
  const [botMessagesDisplayed, setBotMessagesDisplayed] = useState([]);
  const [botMessagesDisplayTimerRef, setBotMessagesDisplayTimerRef] = useState(
    []
  );
  const [transcriptVisible, setTranscriptVisible] = useState(false);
  const [isSwitchOn, setIsSwitchOn] = useState(false);
  const [isCaptionVisible, setIsCaptionVisible] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [mediaStream, setMediaStream] = useState(null);
  const [audioContext, setAudioContext] = useState(null);
  const [destination, setDestination] = useState(null);
  const [unusedMediaStreamTracks, setUnusedMediaStreamTracks] = useState([]);
  const [recorder, setRecorder] = useState(null);
  const [blobsToUpload, setBlobsToUpload] = useState([]);
  const [chunkIndex, setChunkIndex] = useState(0);
  const [isModelLoading, setIsModelLoading] = useState(
    !(is2D || mode !== SessionMode.WEB_VIDEO)
  );
  // const [screenSize, setScreenSize] = useState("");
  const [disableMic, setDisableMic] = useState(false);
  const [displayCandidateVisualizer, setDisplayCandidateVisualizer] =
    useState(false);
  const [isBotSpeaking, setIsBotSpeaking] = useState(false);
  const [lastAgentTranscriptTime, setLastAgentTranscriptTime] = useState(null);

  // Ultravox client state
  const ultravoxSessionRef = useRef(null);
  // Avatar video interviews get a shorter cap than the other modes - HeyGen
  // LiveAvatar sessions are more expensive to run long than voice-only ones.
  const interviewDurationMinutes = mode === SessionMode.WEB_VIDEO ? 19 : 59;
  // Warning/danger timer colors as fractions of the total, so they scale with
  // interviewDurationMinutes instead of being tied to the old fixed 59:59 cap
  // (15/60 and 5/60 reproduce the original thresholds for that default).
  const timerWarningMinutes = Math.round((interviewDurationMinutes + 1) * (15 / 60));
  const timerDangerMinutes = Math.round((interviewDurationMinutes + 1) * (5 / 60));
  const [interviewTime, setInterviewTime] = useState({
    minutes: interviewDurationMinutes,
    seconds: 59,
  });
  const [
    showGetScreenSharePermissionModal,
    setShowGetScreenSharePermissionModal,
  ] = useState({
    isModal: false,
    message: "",
    isBtn1: false,
    isBtn2: true,
    btn1: "Cancel",
    btn2: "Allow",
    btn2Function: () => {},
    isLoading: false,
  });

  const { isSynthesizing, isRecognizing, setIsRecognizing } =
    useBlendShapeStore();

  // Bot speaking detection function
  const detectBotSpeakingState = (transcripts) => {
    if (!transcripts || transcripts.length === 0) {
      setIsBotSpeaking(false);
      return;
    }

    const agentTranscripts = transcripts.filter((t) => t.speaker === "agent");
    const latestAgentTranscript = agentTranscripts[agentTranscripts.length - 1];

    if (latestAgentTranscript) {
      const now = Date.now();

      // Bot is speaking if there's a recent agent transcript that's not final
      if (!latestAgentTranscript.isFinal && latestAgentTranscript.text) {
        setIsBotSpeaking(true);
        setLastAgentTranscriptTime(now);
        // console.log(
        //   "[Bot Speaking] Bot started speaking:",
        //   latestAgentTranscript.text
        // );
      } else if (latestAgentTranscript.isFinal) {
        // Give a small buffer time after final transcript before considering bot stopped
        setTimeout(() => {
          setIsBotSpeaking(false);
          // console.log("[Bot Speaking] Bot stopped speaking");
        }, 800); // 800ms buffer to account for processing delays
      }
    }
  };

  // Auto-stop detection if no new transcripts for a while
  useEffect(() => {
    if (lastAgentTranscriptTime) {
      const timeout = setTimeout(() => {
        setIsBotSpeaking(false);
        console.log("[Bot Speaking] Auto-stopped due to inactivity");
      }, 3000); // 3 seconds timeout

      return () => clearTimeout(timeout);
    }
  }, [lastAgentTranscriptTime]);

  useEffect(() => {
    if (DeepgramConfig.DEBUG_LOGGING) {
      console.log("Deepgram STT : realtimeTranscript : ", realtimeTranscript);
      console.log("Deepgram STT : error : ", deepgramError);
      console.log("Deepgram STT : connected : ", deepgramConnected);
      console.log("Deepgram STT : listening : ", deepgramListening);
      console.log("Deepgram STT : ------------ ");
    }
  }, [realtimeTranscript, deepgramError, deepgramConnected, deepgramListening]);

  // Debug logging for bot speaking state changes
  useEffect(() => {
    console.log("[Bot Speaking] State changed:", isBotSpeaking);
  }, [isBotSpeaking]);

  const navigate = useNavigate();

  useEffect(() => {
    if (audioContext?.state !== "running") {
      const audioContext = new AudioContext();
      const destination = audioContext.createMediaStreamDestination();
      setAudioContext(audioContext);
      setDestination(destination);
    }
  }, [audioContext]);

  useEffect(() => {
    const mediaQueries = {
      phone: window.matchMedia("(max-width: 767px)"),
      tablet: window.matchMedia("(min-width: 768px) and (max-width: 1023px)"),
      desktop: window.matchMedia("(min-width: 1024px)"),
    };

    const updateDeviceType = () => {
      if (mediaQueries.phone.matches) setIsDesktop(false);
      else if (mediaQueries.tablet.matches) setIsDesktop(false);
      else setIsDesktop(true);
    };

    updateDeviceType();

    Object.values(mediaQueries).forEach((mq) =>
      mq.addEventListener("change", updateDeviceType)
    );

    return () => {
      Object.values(mediaQueries).forEach((mq) =>
        mq.removeEventListener("change", updateDeviceType)
      );
    };
  }, []);

  // -------------------------------- visualiser ---------------------------------------

  const canvasBotRef = useRef(null);
  const canvasRef = useRef(null);
  const containerBotRef = useRef(null);
  const containerRef = useRef(null);
  const [radius, setRadius] = useState(0);
  const [botRadius, setBotRadius] = useState(0);

  // ------------ set radius ---------------
  // --------------------- candidate radius -----------------------
  useEffect(() => {
    // Dynamically calculate the radius based on the container size
    const updateRadius = () => {
      if (containerRef.current) {
        const containerSize = containerRef.current.offsetWidth;
        setRadius(containerSize / 1);
      }
    };

    // Update radius on mount and window resize
    updateRadius();
    window.addEventListener("resize", updateRadius);

    return () => {
      window.removeEventListener("resize", updateRadius);
    };
  }, [containerRef.current]);
  // // --------------------- bot radius -----------------------
  useEffect(() => {
    // Dynamically calculate the radius based on the container size
    const updateBotRadius = () => {
      if (containerBotRef.current) {
        const containerSize = containerBotRef.current.offsetWidth;
        setBotRadius(containerSize / 1);
      }
    };

    // Update radius on mount and window resize
    updateBotRadius();
    window.addEventListener("resize", updateBotRadius);

    return () => {
      window.removeEventListener("resize", updateBotRadius);
    };
  }, []);

  // ------------ set visualizer ---------------
  // --------------------- candidate visualizer -----------------------
  useEffect(() => {
    if (
      !radius ||
      !permissions.micStream.stream ||
      isMuted ||
      !canvasRef.current
    )
      return; // Wait for radius to be calculated

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // Set canvas size dynamically
    canvas.width = radius * 4;
    canvas.height = radius * 4;

    // Set up audio context and analyser
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 256; // Number of frequency bins
    analyserRef.current = analyser;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    dataArrayRef.current = dataArray;

    const smoothDataArray = new Float32Array(dataArray.length); // For smoothing
    const smoothingFactor = 0.9; // Adjust for more/less smoothing

    // Use the userAudioStream from state
    const source = audioCtx.createMediaStreamSource(
      // permissions.userAudioForDisplayStream?.stream
      permissions.micStream?.stream
    );
    source.connect(analyser);

    const draw = () => {
      if (isMuted) {
        return;
      }
      requestAnimationFrame(draw);

      analyser.getByteFrequencyData(dataArray);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      // ----------------------- circular bar pattern -------------------------------
      const barCount = 120; // Number of visualizer bars
      const barWidth = 0.8;
      const maxBarHeight = 100;

      for (let i = 0; i < barCount; i++) {
        const angle = (i / barCount) * Math.PI * 2;

        // Smooth the data using linear interpolation
        smoothDataArray[i] =
          smoothDataArray[i] * smoothingFactor +
          dataArray[i] * (1 - smoothingFactor);

        let barLength = dataArray[i] / 2.5; // Adjust bar length based on audio data
        barLength = Math.min(barLength, maxBarHeight); // Limit max bar length

        const x1 = centerX + Math.cos(angle) * radius;
        const y1 = centerY + Math.sin(angle) * radius;

        const x2 = centerX + Math.cos(angle) * (radius + barLength);
        const y2 = centerY + Math.sin(angle) * (radius + barLength);

        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.strokeStyle = "#101439"; // Bar color
        ctx.lineWidth = barWidth;
        ctx.stroke();
      }
    };

    draw();

    return () => {
      audioCtx.close();
    };
  }, [radius, isMuted, permissions.micStream]);
  // // --------------------- bot visualizer -----------------------
  useEffect(() => {
    if (
      !botRadius ||
      !canvasBotRef.current ||
      !isBotSpeaking // Only show visualizer when bot is speaking
    )
      return; // Wait for botRadius to be calculated and bot to be speaking

    const canvas = canvasBotRef.current;
    const ctx = canvas.getContext("2d");

    // Set canvas size dynamically
    canvas.width = botRadius * 3;
    canvas.height = botRadius * 3;

    // ------------------------- Realistic voice output visualizer --------------------------
    // Simulate realistic speech patterns with dynamic spikes and voice-like frequencies
    const barCount = 120; // Number of visualizer bars
    const dataArray = new Uint8Array(barCount); // Simulated data array
    const smoothDataArray = new Float32Array(barCount); // For smoothing
    const targetDataArray = new Float32Array(barCount); // Target values for smoother transitions
    const smoothingFactor = 0.75; // Adjusted for more responsive voice patterns
    let animationId;
    let lastFrameTime = 0;
    const targetFPS = 60;
    const frameInterval = 1000 / targetFPS;

    // Voice simulation parameters
    let speechIntensity = 0;
    let speechPhase = 0;
    let vowelEmphasis = 0;
    let consonantSpikes = [];
    let breathingPattern = 0;

    // Initialize arrays
    for (let i = 0; i < barCount; i++) {
      smoothDataArray[i] = 0;
      targetDataArray[i] = 0;
    }

    // Initialize consonant spike positions (simulate speech articulation)
    for (let i = 0; i < 8; i++) {
      consonantSpikes.push({
        position: Math.random() * barCount,
        intensity: 0,
        decay: 0.95,
      });
    }

    const draw = (currentTime) => {
      if (!isBotSpeaking) {
        // Gradually fade out when bot stops speaking
        for (let i = 0; i < barCount; i++) {
          smoothDataArray[i] *= 0.92; // Smooth fade out
          targetDataArray[i] *= 0.92;
        }

        // Check if animation should stop (all values near zero)
        const maxValue = Math.max(...smoothDataArray);
        if (maxValue < 1) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          return;
        }
      }

      // Throttle frame rate for smoother animation
      if (currentTime - lastFrameTime < frameInterval) {
        animationId = requestAnimationFrame(draw);
        return;
      }
      lastFrameTime = currentTime;

      animationId = requestAnimationFrame(draw);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const maxBarHeight = 120; // Increased height for better visual impact
      const time = currentTime * 0.001; // Convert to seconds for wave effect

      // Update speech simulation parameters
      if (isBotSpeaking) {
        speechIntensity = Math.min(speechIntensity + 0.05, 1.0);
        speechPhase += 0.15;
        vowelEmphasis = Math.sin(time * 1.5) * 0.5 + 0.5;
        breathingPattern = Math.sin(time * 0.8) * 0.3 + 0.7;

        // Randomly trigger consonant spikes (simulate speech articulation)
        if (Math.random() < 0.08) {
          const spike =
            consonantSpikes[Math.floor(Math.random() * consonantSpikes.length)];
          spike.intensity = 1.0;
          spike.position = Math.random() * barCount;
        }
      } else {
        speechIntensity = Math.max(speechIntensity - 0.03, 0);
      }

      // Update consonant spikes
      consonantSpikes.forEach((spike) => {
        spike.intensity *= spike.decay;
      });

      for (let i = 0; i < barCount; i++) {
        if (isBotSpeaking || speechIntensity > 0) {
          // Simulate realistic voice frequency distribution
          const frequency = i / barCount;

          // Low frequencies (fundamental voice tone) - stronger for vowels
          const fundamentalFreq =
            frequency < 0.15
              ? (Math.sin(speechPhase + i * 0.2) * vowelEmphasis * 0.8 + 0.4) *
                breathingPattern
              : 0;

          // Mid frequencies (formants) - voice character
          const formantFreq =
            frequency >= 0.15 && frequency < 0.5
              ? Math.sin(time * 2.5 + i * 0.15) * 0.6 * vowelEmphasis
              : 0;

          // High frequencies (consonants and sibilants) - sharp spikes
          let consonantFreq = 0;
          consonantSpikes.forEach((spike) => {
            const distance = Math.abs(i - spike.position);
            if (distance < 8) {
              consonantFreq +=
                spike.intensity * Math.exp(-distance * 0.3) * 0.7;
            }
          });

          // Breathing and natural speech variation
          const naturalVariation =
            Math.sin(time * 4 + i * 0.08) * 0.2 * breathingPattern;

          // Random micro-variations for realism
          const microVariation = (Math.random() - 0.5) * 0.15;

          // Combine all components with speech intensity
          targetDataArray[i] =
            (fundamentalFreq +
              formantFreq +
              consonantFreq +
              naturalVariation +
              microVariation) *
            speechIntensity *
            200;
        } else {
          targetDataArray[i] *= 0.9; // Fade out when not speaking
        }

        // Apply double smoothing for ultra-smooth animation
        // (Remove the buggy target smoothing for now - it was causing the visualizer to not show)

        smoothDataArray[i] =
          smoothDataArray[i] * smoothingFactor +
          targetDataArray[i] * (1 - smoothingFactor);

        const angle = (i / barCount) * Math.PI * 2;
        let barLength = smoothDataArray[i] / 2.0; // Adjusted for voice patterns
        barLength = Math.min(barLength, maxBarHeight); // Limit max bar length
        barLength = Math.max(barLength, isBotSpeaking ? 3 : 1); // Dynamic minimum based on speaking state

        const x1 = centerX + Math.cos(angle) * botRadius;
        const y1 = centerY + Math.sin(angle) * botRadius;

        const x2 = centerX + Math.cos(angle) * (botRadius + barLength);
        const y2 = centerY + Math.sin(angle) * (botRadius + barLength);

        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);

        // Dynamic styling based on frequency and intensity
        const frequency = i / barCount;
        const intensity = barLength / maxBarHeight;

        // Color variation based on frequency (voice characteristics)
        let red = 16,
          green = 20,
          blue = 57;
        if (frequency < 0.15) {
          // Low frequencies - warmer tone
          red += intensity * 30;
          green += intensity * 15;
        } else if (frequency > 0.6) {
          // High frequencies - cooler, sharper tone
          blue += intensity * 40;
          green += intensity * 20;
        }

        const opacity = 0.6 + intensity * 0.4;
        ctx.strokeStyle = `rgba(${Math.min(red, 255)}, ${Math.min(
          green,
          255
        )}, ${Math.min(blue, 255)}, ${opacity})`;
        ctx.lineWidth = 1.0 + intensity * 0.8; // Dynamic line width based on intensity
        ctx.lineCap = "round"; // Rounded ends for smoother appearance
        ctx.stroke();
      }
    };

    draw(performance.now());

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [botRadius, isBotSpeaking]);

  // console.log("isMuted : ", isMuted);

  // --------------------------------------------------------------------------

  // CLEAN-UP
  useEffect(() => {
    if (interviewEnded) {
      recorder?.stop();
      if (ultravoxSessionRef.current) {
        console.log("Leaving Ultravox Session : ", ultravoxSessionRef.current);
        ultravoxSessionRef.current.leaveCall();
      }
    }
  }, [interviewEnded]);
  useEffect(() => {
    if (userWebcamVideoRef.current && permissions.webcam.stream) {
      userWebcamVideoRef.current.setVideoStreamSrcObject(
        permissions.webcam.stream
      );
    }
  }, [permissions.webcam.stream]);

  useEffect(() => {
    const timer = setInterval(() => {
      setInterviewTime((prevTime) => {
        let newMinutes = prevTime.minutes;
        let newSeconds = prevTime.seconds;
        if(prevTime.minutes>0 ||prevTime.seconds>0){
          if(prevTime.seconds ===0){
            prevTime.seconds = 60
          } 
          newSeconds = prevTime.seconds - 1;
          newMinutes = prevTime.minutes - Math.floor(prevTime.seconds / 60);
        }
        
        return {
          minutes: newMinutes,
          seconds: newSeconds % 60,
        };
      });
    }, 1000);

    // Cleanup function to clear the interval when the component unmounts
    return () => {
      clearInterval(timer);
    };
  }, []);

  useEffect(()=>{
    if(interviewTime.minutes===0 && interviewTime.seconds===0){
      handleLeave();
    }
  }, [interviewTime])

  useEffect(() => {
    if (botResponse) {
      setBotMessagesDisplayed([botResponse]);
    }
  }, [botResponse]);

  const clearAllTimeouts = (botMessagesDisplayTimerRef) => {
    botMessagesDisplayTimerRef?.forEach((id) => clearTimeout(id));
    setBotMessagesDisplayTimerRef([]);
  };

  const handleTranscriptToggle = (value) => {
    console.log("ischecked : ", value);

    setTranscriptVisible(value);
    setIsSwitchOn(value);
  };

  const toggleCaption = () => {
    setIsCaptionVisible(!isCaptionVisible);
  };

  const toggleIs2D = () => {
    setIs2D(!is2D);
  };

  const exportBlob = async ({ data: blob }) => {
    try {
      console.log(
        "ExportBlob : Blob : ",
        blob,
        " | Blob size : ",
        blob?.size,
        " | meetingId : ",
        meetingId,
        " | sasUrl : ",
        sasUrl
      );
      if (blob?.size > 0) {
        const currentMills = Date.now();
        let duration = currentMills - lastRecordingTimestamp;
        lastRecordingTimestamp = currentMills;

        blob = await fixWebmDuration(blob, duration, { logger: false });
        console.log("ExportBlob : Fixed webm duration");

        if (meetingId && sasUrl) {
          // Add current blob to the queue for sequential processing with chunk index
          const blobWithIndex = { blob, index: chunkIndex };
          const allBlobsToProcess = [...blobsToUpload, blobWithIndex];

          // Increment chunk index for next blob
          setChunkIndex((prev) => prev + 1);

          // Process all blobs concurrently with Promise.allSettled
          const uploadPromises = allBlobsToProcess.map((blobWithIndex, i) =>
            uploadBlob(
              blobWithIndex.blob,
              meetingId,
              blobWithIndex.index,
              storageClient
            )
              .then(() => ({
                success: true,
                index: blobWithIndex.index,
                originalIndex: i,
              }))
              .catch((error) => ({
                success: false,
                index: blobWithIndex.index,
                originalIndex: i,
                error,
              }))
          );

          const results = await Promise.allSettled(uploadPromises);
          let remainingBlobs = [];
          let hasFailures = false;

          results.forEach((result, i) => {
            if (result.status === "fulfilled") {
              const uploadResult = result.value;
              if (uploadResult.success) {
                console.log(
                  `ExportBlob : Staged block chunk ${uploadResult.index} uploaded successfully`
                );
              } else {
                console.error(
                  `ExportBlob : Failed to stage block chunk ${uploadResult.index}:`,
                  uploadResult.error
                );
                remainingBlobs.push(
                  allBlobsToProcess[uploadResult.originalIndex]
                );
                hasFailures = true;
              }
            } else {
              // Promise itself was rejected (shouldn't happen with our catch above, but just in case)
              console.error(
                `ExportBlob : Promise rejected for blob ${i}:`,
                result.reason
              );
              remainingBlobs.push(allBlobsToProcess[i]);
              hasFailures = true;
            }
          });

          // Handle session end if leaving and no failures
          if (isLeaving && !hasFailures) {
            console.log("ExportBlob : isLeaving - ", isLeaving);
            // tenantMngtClient?.endSession({ meetingId }).finally(() => {
            //   window.localStorage.clear();
            //   window.sessionStorage.clear();
            // });
          }

          // Refresh SAS URL if there were any failures
          if (hasFailures) {
            botClient.getSasUrl();
          }

          // Update the queue with remaining blobs (failed + unprocessed)
          setBlobsToUpload(remainingBlobs);
        } else {
          const blobWithIndex = { blob, index: chunkIndex };
          setBlobsToUpload((prev) => [...prev, blobWithIndex]);
          setChunkIndex((prev) => prev + 1);
        }
      }
    } catch (e) {
      console.error("ExportBlob : Error while exporting the blob - ", e);
    }
  };

  function startRecording() {
    try {
      console.log(
        "MediaStream tracks: Before Initializing MediaRecorder : ",
        mediaStream.getTracks()
      );
      const mediaRecorder = new MediaRecorder(mediaStream, {
        mimeType: MediaRecorderConfig.mediaType,
        // audioBitsPerSecond: 128000, // 128kbps
        // videoBitsPerSecond: 512000, // 512kbps
        audioBitsPerSecond: 128000, // 128kbps
        videoBitsPerSecond: 256000, // 256kbps
      });

      // Set event handler immediately after creation
      mediaRecorder.ondataavailable = exportBlob;
      console.log("ondataavailable registered in startRecording");

      setRecorder(mediaRecorder);
      console.log("MediaRecorder Initialized");
    } catch (e) {
      console.error("MediaRecorder Initialization Failed - ", e);
      Sentry.captureException(e);
    }
  }

  useEffect(() => {
    if (recorder) {
      recorder.ondataavailable = exportBlob;
      console.log("ondataavailable registered");
    }
  }, [recorder, meetingId, sasUrl, storageClient, blobsToUpload, chunkIndex]);

  useEffect(() => {
    if (sasUrl) {
      let client = new AzureBlobStorageClient(sasUrl);
      setStorageClient(client);
    }
  }, [sasUrl]);

  function stopAllMediaTracks() {
    console.log("Stopping tracks");
    permissions.webcam?.stream?.getTracks().forEach((track) => track.stop());
    if (userWebcamVideoRef.current) {
      userWebcamVideoRef.current.srcObject = null;
    }
    unusedMediaStreamTracks?.forEach((track) => track.stop());
    mediaStream?.getTracks().forEach((track) => track.stop());
    audioContext?.close();
  }
  // console.log("Recorder Object - ", recorder);
  useEffect(() => {
    if (!recorder && mediaStream) {
      startRecording();
    } else if (recorder && recorder.state === "inactive") {
      try {
        // Add debugging info
        console.log(
          "Starting recorder with 30s timeslice. Recorder state:",
          recorder.state
        );
        console.log(
          "MediaStream tracks: Before recorder.start",
          mediaStream?.getTracks()
        );

        // recorder.start(30000);
        recorder.start(10000);
        console.log("Recording Started");
      } catch (e) {
        console.error("Recorder Error -", e);
        Sentry.captureException(e);
      }
    }
  }, [mediaStream, recorder]);

  useEffect(() => {
    botClient
      .getSasUrl()
      .then((response) => {
        setSasUrl(response?.sas_url);
      })
      .catch(() => {});
    return () => {};
  }, [botClient]);

  useEffect(() => {
    let timeout;
    if (endInterviewAfterSynthesis && !isSynthesizing) {
      timeout = setTimeout(() => {
        // setAllowUserCamera(false);
        setInterviewEnded(true);
      }, 500);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [endInterviewAfterSynthesis, isSynthesizing]);

  useEffect(() => {
    if (
      !mediaStream &&
      // canInitializeMediaStream &&
      (["v1", "v2"].includes(version) ||
        (mode !== SessionMode.WEB_VIDEO &&
          (botAudioOnlyStream || isUltravoxAgentNodeAvailable)) ||
        avatarVideoStream)
    ) {
      startStream();
    }
  }, [
    avatarVideoStream,
    botAudioOnlyStream,
    mediaStream,
    mode,
    isUltravoxAgentNodeAvailable,
  ]);

  const getScreenSharingStream = async () => {
    let displayStream = permissions.tabDisplayRecording.stream;

    let userAudioStream = permissions.userAudioForDisplayStream.stream;

    // Check if display stream contains audio tracks
    if (
      ["v1", "v2"].includes(version) &&
      !displayStream.getAudioTracks().length
    ) {
      console.log("Tab Audio is mandatory. Please enable audio sharing.");
      displayStream.getTracks().forEach((track) => track.stop());
      // alert("Please enable audio sharing in your screen sharing settings.");

      setShowGetScreenSharePermissionModal((prev) => ({
        ...prev,
        isModal: true,
        isLoading: false,
        message:
          "Please enable audio sharing in your screen sharing settings and retry.",
        isBtn1: false,
        isBtn2: false,
      }));
      let error = new Error("Tab Audio is mandatory");
      Sentry.captureException(error);
      throw error;
    }

    let displaySource;
    let avatarSource;

    // Only create audio source if we have a valid audio stream
    if (userAudioStream) {
      const userAudioSource =
        audioContext.createMediaStreamSource(userAudioStream);
      userAudioSource.connect(destination);
    }
    if (mode === SessionMode.WEB_VIDEO && avatarVideoStream) {
      avatarSource = audioContext.createMediaStreamSource(avatarVideoStream);
      avatarSource.connect(destination);
    }
    //============= Commented while code cleanup, test and remove =====================
    // else if (!GlobalConfig.IsUltravoxEnabled) {
    //   displaySource = audioContext.createMediaStreamSource(displayStream);
    //   displaySource.connect(destination);
    // }
    let unusedMediaTracks = [];
    displayStream.getAudioTracks().forEach((track) => {
      displayStream.removeTrack(track);
      unusedMediaTracks.push(track);
    });
    if (isSafari) displayStream.addTrack(userAudioStream.getAudioTracks()[0]);
    else displayStream.addTrack(destination.stream.getAudioTracks()[0]);

    // Detect when the user stops sharing the screen
    displayStream.getVideoTracks()[0].onended = () => {
      console.log("User stopped screen sharing.");
      setPermission("tabDisplayRecording", false, displayStream);
    };
    setUnusedMediaStreamTracks(unusedMediaTracks);
    return [displayStream, audioContext];
  };

  const getVoiceOnlyStream = async () => {
    let userAudioStream = permissions.micStream.stream;
    console.log("userAudioStream - ", userAudioStream);
    console.log("botAudioOnlyStream - ", botAudioOnlyStream);
    const userAudioSource =
      audioContext.createMediaStreamSource(userAudioStream);
    userAudioSource.connect(destination);

    //============= Commented while code cleanup, test and remove =====================
    // if (!GlobalConfig.IsUltravoxEnabled && botAudioOnlyStream) {
    //   const botAudioSource =
    //     audioContext.createMediaStreamSource(botAudioOnlyStream);
    //   botAudioSource.connect(destination);
    // }
    console.log("destination.stream - ", destination.stream);
    return [destination.stream, audioContext];
  };

  const getUserVideoStream = async () => {
    let userVideoStream = permissions.webcam.stream;
    let userAudioStream =
      permissions.micStream?.stream || permissions.microphone?.stream;
    console.log("userVideoStream - ", userVideoStream);
    console.log("userAudioStream - ", userAudioStream);

    // Only create audio source if we have a valid audio stream
    if (userAudioStream) {
      const userAudioSource =
        audioContext.createMediaStreamSource(userAudioStream);
      userAudioSource.connect(destination);
    }
    if (mode === SessionMode.WEB_VIDEO && avatarVideoStream) {
      const avatarAudioSource =
        audioContext.createMediaStreamSource(avatarVideoStream);
      avatarAudioSource.connect(destination);
    }

    let mediaStream = new MediaStream();
    mediaStream.addTrack(userVideoStream.getVideoTracks()[0]);
    mediaStream.addTrack(destination.stream.getAudioTracks()[0]);
    console.log("mediaStream - ", mediaStream);
    return [mediaStream, audioContext];
  };

  const startStream = async () => {
    try {
      console.log("startStream function called.");

      let stream, audioCtx;
      if (isDesktop) {
        [stream, audioCtx] = await getScreenSharingStream();
      } else if (mode === SessionMode.WEB_AUDIO) {
        MediaRecorderConfig.setAudioOnlyMedia();
        [stream, audioCtx] = await getVoiceOnlyStream();
      } else {
        [stream, audioCtx] = await getUserVideoStream();
      }

      setMediaStream(stream);
      console.log("New Media Stream set -", stream);

      setShowGetScreenSharePermissionModal((prev) => ({
        ...prev,
        isModal: false,
        isLoading: false,
        message: "",
        isBtn1: false,
        isBtn2: true,
        btn2: "Proceed",
        btn2Function: () => {
          setShowGetScreenSharePermissionModal((e) => ({
            ...e,
            isModal: false,
          }));
        },
      }));
      return stream;
    } catch (e) {
      console.log(
        "startScreenRecording Failed -",
        e,
        Object.prototype.toString.call(e)
      );

      // Handle specific DOMException errors
      if (e instanceof DOMException) {
        let message = "";
        // let btn2Function = () => getMediaPermissions();
        let isBtn1 = false;
        let isBtn2 = true;
        let btn1 = "";
        let btn2 = "Okay";

        switch (e.name) {
          case "NotAllowedError":
            message =
              "Screen sharing access was denied. Please enable screen sharing permissions.";
            break;
          case "NotFoundError":
            message =
              "No media devices found. Please check your camera and microphone connections.";
            // btn2Function = () => {}; // No retry option
            break;
          case "SecurityError":
            message =
              "Your browser's security settings are preventing access to media devices. Please adjust your security settings and try again.";
            break;
          default:
            message = `Screen sharing access: ${e.message.toLowerCase()}`;
            break;
        }
      } else {
      }
    }
  };

  const handleLeave = () => {
    // Stop recognition and mute microphone

    setIsRecognizing(false);
    setIsMuted(true);

    // Show the interview end notification screen
    setInterviewEnded(true);
    isLeaving = true;
  };

  const handleActualLeave = async () => {
    navigate("/thankyou");
    sessionStorage.clear();
    stopAllMediaTracks();
  };

  useEffect(() => {
    addMessage(BotConstants.BOT_NAME, botMessages, true);
  }, [botMessages]);

  const handleTempInputChange = (tempInputValue) => {
    setUserResponse(null);

    const tempInputValueArray = [
      ...(interruptedInputs?.join(" ").split(" ") ?? []),
      ...(tempInputValue?.split(" ") ?? []),
    ];
    if (!userMessageIndex) {
      if (
        tempInputValueArray?.slice(0, -1)?.join(" ").length >=
        (screenSize === "sm"
          ? 70
          : screenSize === "md"
          ? 90
          : screenSize === "lg"
          ? 110
          : screenSize === "xl"
          ? 150
          : 170)
      ) {
        setUserMessageIndex(tempInputValueArray?.length + 1);
        setUserMessage(tempInputValueArray);
      } else {
        setUserMessage(tempInputValueArray);
      }
    } else {
      if (
        tempInputValueArray?.slice(userMessageIndex)?.join(" ").length >=
        (screenSize === "sm"
          ? 70
          : screenSize === "md"
          ? 90
          : screenSize === "lg"
          ? 110
          : screenSize === "xl"
          ? 150
          : 170)
      ) {
        setUserMessageIndex(tempInputValueArray?.length + 1);
        setUserMessage(tempInputValueArray?.slice(userMessageIndex));
      } else {
        setUserMessage(tempInputValueArray?.slice(userMessageIndex));
      }
    }
  };
  function getUserResponseWithoutBreakingWord(text) {
    console.log("getUserResponseWithoutBreakingWord : text : ", text);
    let charactersToDisplay =
      screenSize === "sm"
        ? -70
        : screenSize === "md"
        ? -90
        : screenSize === "lg"
        ? -110
        : screenSize === "xl"
        ? -150
        : -170;
    let trimmedText = text.slice(charactersToDisplay);

    console.log(
      "getUserResponseWithoutBreakingWord : trimmedText : initial : ",
      trimmedText
    );

    const firstSpaceIndex = trimmedText.indexOf(" ");
    trimmedText =
      firstSpaceIndex !== -1
        ? trimmedText.slice(firstSpaceIndex + 1)
        : trimmedText;

    console.log(
      "getUserResponseWithoutBreakingWord : trimmedText : ",
      trimmedText
    );
    if (trimmedText?.length > text?.length + 10) {
      trimmedText = ".." + trimmedText;
    }

    return trimmedText;
  }

  const handleUserSpeech = (speechText) => {
    if (
      speechText?.trim() !== "" ||
      speechText !== undefined ||
      speechText !== ""
    ) {
      setUserResponse(getUserResponseWithoutBreakingWord(speechText));
    }
  };

  const toggleRecognition = () => {
    // setIsRecognizing(!isRecognizing);
    setIsMuted((e) => {
      if (e) {
        //If muted then unmute
        setIsRecognizing(true);

        // Resume Deepgram recording if it's connected but paused
        if (DeepgramConfig.ENABLED && deepgramConnected && deepgramPaused) {
          resumeDeepgram();
        }
        // Start Deepgram if not connected at all
        else if (DeepgramConfig.ENABLED && !deepgramListening) {
          startDeepgram();
        }

        if (ultravoxSessionRef.current) {
          ultravoxSessionRef.current.unmuteMic();
        } else if (avatar) {
          avatar.unmuteInputAudio();
        }
      } else {
        //else mute
        setIsRecognizing(false);

        // Pause Deepgram recording (keep WebSocket connection alive)
        if (deepgramListening && !deepgramPaused) {
          pauseDeepgram();
        }

        if (ultravoxSessionRef.current) {
          ultravoxSessionRef.current.muteMic();
        } else if (avatar) {
          avatar.muteInputAudio();
        }
      }
      return !e;
    });
    console.log("is mute ------- : ", !isMuted);
    console.log("is recognizing ------- : ", !isRecognizing);
    console.log("deepgram listening ------- : ", deepgramListening);
    console.log("deepgram paused ------- : ", deepgramPaused);
  };

  useEffect(() => {
    if (botMessagesDisplayed?.length > 0 /* && !isDesktop */) {
      let maxCharacters =
        screenSize === "sm"
          ? 100
          : screenSize === "md"
          ? 150
          : screenSize === "lg"
          ? 250
          : screenSize === "xl"
          ? 350
          : 250;
      if (
        botMessagesDisplayed?.join(" ")?.length >= maxCharacters
        // max you can set 180 characters
      ) {
        console.log("bot Messages : screen size -", botMessages, screenSize);
        setBotMessagesDisplayed([]);
      }
    }
  }, [
    botMessagesDisplayed,
    screenSize,
    botMessages,
    version /* , isDesktop */,
  ]);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      let newSize = "md";

      if (width >= 1200) {
        newSize = "xl";
      } else if (width >= 992) {
        newSize = "lg";
      } else if (width >= 768) {
        newSize = "md";
      } else {
        newSize = "sm";
      }

      setScreenSize(newSize);
    };

    handleResize(); // Initial call to set initial screen size

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  // console.log("botMessagesDisplayed : ", botMessagesDisplayed);

  return (
    <>
      <div className="w-full h-screen flex flex-col bg-gray-100">
        {/* ---------------- navbar -------------- */}

        <div className="w-full flex flex-col md:flex-row md:gap-x-5 justify-start items-center md:px-5  pb-3 md:pb-0 pt-3">
          <div className="w-[95%] h-[12vh] min-[100px] md:h-[10vh] min-h-[55px] md:w-full flex flex-col sm:flex-row  bg-app-primaryLight rounded-lg justify-between items-center md:px-2 px-5 pb-2 sm:py-2">
            <div className="flex justify-start items-center w-auto relative">
              <div className="w-32 md:w-44 lg:w-56">
                <img src={logo} alt="Logo" />
              </div>
              <div className="text-black text-xs relative right-[8px]">
                {version}
              </div>
            </div>
            {/* ------------------------- bot - candidate - timer ------------------  */}
            <div className="w-auto flex justify-between md:justify-start items-center gap-x-5">
              <div className="text-app-primary font-medium text-xs md:text-sm">
                {BotConstants.BOT_NAME} & {candidateName} - Technical Round
              </div>
              <div className={`flex items-center justify-start gap-x-2 border border-purple-400 rounded-full px-2 py-[2px] sm:py-[3px] ${interviewTime.minutes <= timerWarningMinutes ? 'animate-pulse' : ''}`}>
                <div className={`w-[4px] h-[4px] sm:w-[6px] sm:h-[6px] rounded-full ${interviewTime.minutes <= timerDangerMinutes ? 'bg-red-600' :interviewTime.minutes <= timerWarningMinutes?'bg-yellow-600': 'bg-green-600' }`}></div>
                <div className="text-[10px] md:text-xs text-app-primary font-medium">
                  {interviewTime.minutes} : {interviewTime.seconds}
                </div>
              </div>
            </div>

            {/* ---------------------------- transcript toggle- mic - leave ------------------------------- */}
            <div className="hidden lg:flex gap-x-3">
              {/* ------------------------- transcript toggle ------------------- */}
              {screenSize !== "sm" && screenSize !== "md" && (
                <div className="transcript-navbar-container flex items-center mr-8">
                  <input
                    type="checkbox"
                    className="toggle toggle-xs "
                    checked={isSwitchOn}
                    onClick={(e) => handleTranscriptToggle(e.target.checked)}
                  />

                  <div className="toggle-transcript-text-of-button text-app-primary text-sm font-medium">
                    Transcript
                  </div>
                </div>
              )}

              <div
                className="bg-app-primary text-white flex items-center justify-center p-[10px] rounded-full cursor-pointer"
                onClick={isRecognizing && disableMic ? null : toggleRecognition}
              >
                {isMuted ? <MicOff size={16} /> : <Mic size={16} />}
              </div>

              <div
                className="bg-app-danger cursor-pointer rounded-full px-5 py-1 text-white text-sm flex items-center justify-center"
                onClick={() => {
                  handleLeave();
                }}
              >
                Leave
              </div>
            </div>
          </div>
        </div>

        {/* ---------------------------------------------- conversation screen ----------------------------------- */}

        <div
          className={`flex-[9] flex sm:items-stretch flex-col items-center sm:flex-row max-h-[88vh] sm:max-h-[88vh]
           
            ${transcriptVisible ? "transcript-visible" : ""}
          `}
        >
          {/* ---------------- bot screen -------------- */}
          {is2D || mode !== SessionMode.WEB_VIDEO ? (
            <section className="bot-screen-container-voice-only relative">
              <div className="absolute top-5 left-5 bg-app-primary text-white px-6 py-1 rounded-full text-[13px] z-30">
                Sofia
              </div>
              <div className="bot-screen-container-voice-only-image-container">
                {/* ----------------------- Canvas for the voice visualizer  ------------------- */}
                <canvas
                  className={`bot-visualizer-canvas ${
                    isBotSpeaking ? "bot-speaking" : "bot-idle"
                  }`}
                  ref={canvasBotRef}
                  style={{
                    opacity: isBotSpeaking ? 1 : 0.2,
                    transition: "opacity 0.3s ease-in-out",
                  }}
                ></canvas>
                <div
                  className="bot-screen-container-voice-only-image"
                  ref={containerBotRef}
                ></div>
              </div>
              <div className="bot-screen-child-container">
                <div className="bot-screen-chat-2">
                  {isCaptionVisible && <>{botMessagesDisplayed?.join("")} </>}
                </div>
              </div>
            </section>
          ) : (
            <section className={"bot-screen-liveavatar-model-container relative"}>
              <div className="absolute top-5 left-5 bg-app-primary text-white px-6 py-1 rounded-full text-[13px] z-30">
                Sofia
              </div>

              <ModelViewerV3
                isModelLoading={(value) => setIsModelLoading(value)}
                setBotMessagesDisplayed={setBotMessagesDisplayed}
              />

              {isCaptionVisible && botMessagesDisplayed.length > 0 && (
                <div className="bot-screen-3D-model-child-container z-40">
                  <div className="bot-screen-3D-model-chat-2 ">
                    {isCaptionVisible && <>{botMessagesDisplayed?.join("")} </>}
                  </div>
                </div>
              )}
            </section>
          )}
          {/* ---------------- candidate screen -------------- */}
          {mode !== SessionMode.WEB_AUDIO ? (
            <section className="candidate-screen-container ">
              <div className="absolute top-5 left-5 bg-app-primary text-white px-6 py-1 rounded-full text-[13px] z-30">
                {firstName}
              </div>
              <UserVideoNew ref={userWebcamVideoRef} />
              {(userResponse || userMessage?.length > 0) && !isMuted && (
                <div className="candidate-screen-text-container z-40">
                  {isCaptionVisible && (
                    <div className="candidate-screen-chat-2">
                      {userResponse ? userResponse : userMessage.join(" ")}
                    </div>
                  )}
                </div>
              )}
            </section>
          ) : (
            <section className="candidate-screen-container-voice-only">
              <div
                className="candidate-screen-container-voice-only-user-image"
                ref={containerRef}
              >
                {/* ----------------------- Canvas for the voice visualizer  ------------------- */}
                {!isMuted && (
                  <canvas
                    className="candidate-visualizer-canvas"
                    ref={canvasRef}
                  ></canvas>
                )}
                <div className="candidate-screen-container-voice-only-user-initial">
                  {candidateName.charAt(0).toUpperCase()}
                </div>
              </div>
              {(userResponse || userMessage?.length > 0) && !isMuted && (
                <div className="candidate-screen-text-container">
                  {isCaptionVisible && (
                    <div className="candidate-screen-chat-2">
                      {userResponse ? userResponse : userMessage.join(" ")}
                    </div>
                  )}
                </div>
              )}
            </section>
          )}
          {/* ---------------- transcript screen -------------- */}

          <section className="conversation-transcript-container bg-gradient-to-b from-[#4776e6] to-[#8E54E9]">
            <div className="conversation-transcript-heading-and-cut-container ">
              <div className=" text-sm text-white font-medium">Transcript</div>
              <X
                size={16}
                className="hover:text-app-danger cursor-pointer text-white hover:scale-110 transition-all duration-300"
                onClick={() => {
                  setIsSwitchOn(false);
                  setTranscriptVisible(false);
                }}
              />
            </div>

            {/* ---------------- transcript chat box of bot -------------- */}

            <TranscriptNew conversations={conversations} />
          </section>

          {/* ---------------------- mic and leave for small screen ------------------------ */}

          <div className="flex xs480:hidden justify-center items-center gap-x-5 bg-app-primaryLight rounded-md py-2 w-[95%]">
            <div
              className="bg-app-primary text-white flex items-center justify-center p-[10px] rounded-full cursor-pointer"
              onClick={isRecognizing && disableMic ? null : toggleRecognition}
            >
              {isMuted ? <MicOff size={16} /> : <Mic size={16} />}
            </div>
            <div
              className="bg-app-danger cursor-pointer rounded-full px-5 py-2 text-white text-sm flex items-center justify-center"
              onClick={() => {
                handleLeave();
              }}
            >
              Leave
            </div>
          </div>
        </div>
        <div className="w-full flex items-center justify-center">
          <div className="hidden xs480:flex lg:hidden justify-center items-center gap-x-5 bg-app-primaryLight rounded-md py-2 w-[95%]">
            <div
              className="bg-app-primary text-white flex items-center justify-center p-[10px] rounded-full cursor-pointer"
              onClick={isRecognizing && disableMic ? null : toggleRecognition}
            >
              {isMuted ? <MicOff size={16} /> : <Mic size={16} />}
            </div>
            <div
              className="bg-app-danger cursor-pointer rounded-full px-5 py-2 text-white text-sm flex items-center justify-center"
              onClick={() => {
                handleLeave();
              }}
            >
              Leave
            </div>
          </div>
        </div>
      </div>
      {/* get screen share permission modal */}

      {showGetScreenSharePermissionModal.isModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <p>{showGetScreenSharePermissionModal.message}</p>
            <div
              className={
                showGetScreenSharePermissionModal.isBtn1
                  ? "modal-buttons"
                  : "modal-buttons-center"
              }
            >
              {showGetScreenSharePermissionModal.isBtn1 && (
                <button
                  onClick={() =>
                    setShowGetScreenSharePermissionModal((prev) => ({
                      ...prev,
                      isModal: false,
                      isLoading: false,
                    }))
                  }
                  className="modal-btn"
                >
                  {showGetScreenSharePermissionModal.btn1}
                </button>
              )}
              {showGetScreenSharePermissionModal.isBtn2 && (
                <button
                  onClick={() =>
                    showGetScreenSharePermissionModal.btn2Function()
                  }
                  className="modal-btn"
                >
                  {showGetScreenSharePermissionModal.isLoading
                    ? "loading.."
                    : showGetScreenSharePermissionModal.btn2}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* --------------------- */}
      <InterviewEndNotification
        tenantMngtClient={tenantMngtClient}
        botClient={botClient}
        interviewEnded={interviewEnded}
        onRedirect={handleActualLeave}
        blobsToUpload={blobsToUpload}
        setBlobsToUpload={setBlobsToUpload}
        storageClient={storageClient}
      />

      {/* Ultravox Client Integration */}
      {GlobalConfig.IsUltravoxEnabled &&
        mode !== SessionMode.WEB_VIDEO &&
        audioContext?.state === "running" &&
        ultravoxJoinUrl && (
          <UltravoxClient
            audioContext={audioContext}
            destinationNode={destination}
            joinUrl={ultravoxJoinUrl}
            onStatusChange={(status, session) => {
              if (!ultravoxSessionRef.current)
                ultravoxSessionRef.current = session;
              console.log("Ultravox status changed:", status);

              // Handle status changes
              if (status === "connected") {
                console.log("Ultravox session connected successfully");
              } else if (status === "error") {
                console.error("Ultravox session error");
              }
            }}
            onTranscriptsUpdate={async (transcripts, session) => {
              // Detect bot speaking state first
              console.log(
                `Latest Ultravox transcript : ${transcripts[transcripts.length - 1].text} | isFinal : ${
                  transcripts[transcripts.length - 1]?.isFinal
                }`
              );
              console.log("Ultravox transcripts: --------\n");
              detectBotSpeakingState(transcripts);

              // Process transcripts and integrate with existing conversation flow
              if (transcripts && transcripts.length > 0) {
                const agentTranscripts = transcripts?.filter(
                  (t) => t.speaker === "agent"
                );
                const latestAgentTranscript =
                  agentTranscripts[agentTranscripts.length - 1];
                if (latestAgentTranscript?.text) {
                  for (let i = 0; i < latestAgentTranscript.text.length; i++) {
                    setBotMessagesDisplayed([latestAgentTranscript.text]);
                  }
                }
                const userTranscripts = transcripts?.filter(
                  (t) => t.speaker === "user"
                );
                const latestUserTranscript =
                  userTranscripts[userTranscripts.length - 1];
                if (latestUserTranscript?.text) {
                  clearAllTimeouts(botMessagesDisplayTimerRef);
                  setUserResponse(
                    getUserResponseWithoutBreakingWord(
                      latestUserTranscript.text
                    )
                  );

                  // // Override Deepgram with Ultravox final transcript
                  // if (DeepgramConfig.ENABLED && override) {
                  //   override(latestUserTranscript.text);
                  // }

                  setDisableMic(false);
                  setDisplayCandidateVisualizer(false);
                }
                let latestFinalTranscript = transcripts
                  ?.filter((t) => t.isFinal)
                  .pop();
                if (lastFinalTranscript?.text !== latestFinalTranscript?.text) {
                  if (latestFinalTranscript?.text) {
                    let isAgent = latestFinalTranscript.speaker === "agent";
                    addMessage(
                      isAgent ? BotConstants.BOT_NAME : candidateName,
                      latestFinalTranscript?.text,
                      isAgent
                    );
                    lastFinalTranscript = latestFinalTranscript;
                  }
                }
              }
            }}
            onSessionReady={(session) => {
              console.log("Ultravox session ready");
              ultravoxSessionRef.current = session;
            }}
            onSessionError={(error, session) => {
              console.error("Ultravox session error:", error);
            }}
            onSessionDisconnect={() => {
              setInterviewEnded(true);
            }}
            autoJoin={true}
            enableTranscripts={true}
          />
        )}
    </>
  );
}

export default ConversationV2;
